// risks.api.ts — T025
// Clients HTTP pour les ressources risques, unités de travail et familles de risque.
// Contrats : specs/001-duerp-gestion-risques/contracts/risks.md

import { INT_TO_BUDGET_TYPE } from "@/domain/enums/measure-budget-type";
import { INT_TO_RESSOURCE_TYPE } from "@/domain/enums/measure-ressource-planning-type";
import type {
  PreventionPlanMeasureSummary,
  PreventionPlanRiskDetails,
} from "@/domain/types/prevention-plan";
import type {
  Risk,
  CreateRiskRequest,
  UpdateRiskRequest,
  RiskListParams,
} from "@/domain/types/risk";
import type { RiskFamily } from "@/domain/types/risk-family";
import type { RiskHistoryEntry } from "@/domain/types/risk-history";
import type { RiskReferential } from "@/domain/types/risk-referential";
import type {
  WorkUnit,
  CreateWorkUnitRequest,
  UpdateWorkUnitRequest,
  RiskType,
} from "@/domain/types/work-unit";

import apiClient from "./client";
import { getWithEtag } from "./etag-cache";
import { API_LIST_PAGE_SIZE, fetchAllPagesFrom } from "./paginated-api";

// ---------------------------------------------------------------------------
// Familles de risque / Référentiels
// ---------------------------------------------------------------------------

// The API may return the family category as an enum number or a string.
type RiskFamilyCategoryApiValue = 0 | 1 | string | null | undefined;

type RiskFamilyApiResponse = Omit<RiskFamily, "category"> & {
  type?: RiskFamilyCategoryApiValue;
  category?: RiskFamilyCategoryApiValue;
};

function mapRiskFamilyCategory(
  value: RiskFamilyCategoryApiValue,
): RiskFamily["category"] {
  if (
    value === 1 ||
    (typeof value === "string" && value.toLowerCase() === "psychosocial")
  ) {
    return "Psychosocial";
  }

  return "Physical";
}

export async function getRiskFamilies(): Promise<RiskFamily[]> {
  const data = await getWithEtag<
    RiskFamilyApiResponse[] | { items: RiskFamilyApiResponse[] }
  >("/api/risk-families", "risk-families");
  const raw = Array.isArray(data) ? data : (data.items ?? []);
  return raw.map((f) => ({
    id: f.id,
    name: f.name,
    displayOrder: f.displayOrder,
    category: mapRiskFamilyCategory(f.category ?? f.type),
  }));
}

export async function getRiskReferentials(
  familyId?: string,
): Promise<RiskReferential[]> {
  const { data } = await apiClient.get<
    RiskReferential[] | { items: RiskReferential[] }
  >("/api/risk-referentials", {
    params: familyId ? { familyId } : undefined,
  });
  return Array.isArray(data) ? data : (data.items ?? []);
}

// ---------------------------------------------------------------------------
// Unités de travail (GET /api/work-units — global, optionnellement filtré par type)
// ---------------------------------------------------------------------------

// L'API retourne `type` comme entier (0 = Physical, 1 = Psychosocial) mais on le transforme en string.
type WorkUnitApiResponse = Omit<
  WorkUnit,
  "type" | "riskCount" | "existingMeasuresCount" | "toBeImplementedMeasuresCount"
> & {
  type?: 0 | 1 | "Physical" | "Psychosocial" | null;
  riskCount?: number;
  existingMeasuresCount?: number;
  toBeImplementedMeasuresCount?: number;
};

function mapWorkUnitType(
  type?: 0 | 1 | "Physical" | "Psychosocial" | null,
): "Physical" | "Psychosocial" | null | undefined {
  if (type === 0 || type === "Physical") return "Physical";
  if (type === 1 || type === "Psychosocial") return "Psychosocial";
  return null;
}

export async function getWorkUnits(params?: {
  siteId?: string;
  type?: RiskType;
}): Promise<WorkUnit[]> {
  const { data } = await apiClient.get<
    WorkUnitApiResponse[] | { items: WorkUnitApiResponse[] }
  >("/api/work-units", { params });
  const raw = Array.isArray(data) ? data : (data.items ?? []);
  return raw.map((wu) => ({
    ...wu,
    type: mapWorkUnitType(wu.type),
    riskCount: wu.riskCount ?? 0,
    existingMeasuresCount: wu.existingMeasuresCount ?? 0,
    toBeImplementedMeasuresCount: wu.toBeImplementedMeasuresCount ?? 0,
  }));
}

export async function createWorkUnit(
  body: CreateWorkUnitRequest,
): Promise<WorkUnit> {
  const { data } = await apiClient.post<WorkUnit>("/api/work-units", body);
  return data;
}

export async function updateWorkUnit(
  workUnitId: string,
  body: UpdateWorkUnitRequest,
): Promise<WorkUnit> {
  const { data } = await apiClient.patch<WorkUnit>(
    `/api/work-units/${workUnitId}`,
    body,
    { headers: { "Content-Type": "application/merge-patch+json" } },
  );
  return data;
}

export async function archiveWorkUnit(workUnitId: string): Promise<void> {
  await apiClient.delete(`/api/work-units/${workUnitId}`);
}

// ---------------------------------------------------------------------------
// Risques
// ---------------------------------------------------------------------------

/**
 * Transforme un corps de requête frontend vers le format attendu par l'API.
 * La Maîtrise est déjà au format décimal (0.1 à 1.0) sur le frontend.
 */
function toApiBody<T extends { control?: number }>(body: T): T {
  return body;
}

// Réponse paginée de l'API risques
interface ApiPaginatedRisks {
  items: Risk[];
  pagination?: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

/**
 * Mappe un risque brut de l'API vers le type domaine Risk.
 * L'API retourne `status: "Active"|"Archived"` et `referentialName` au lieu de `isArchived` et `name`.
 * `archivedAt` est absent de l'API — on utilise `updatedAt` comme approximation.
 */
function mapApiRisk(r: Risk, siteId: string): Risk {
  const raw = r as unknown as Record<string, unknown>;
  const isArchived = raw.status === "Archived";
  return {
    ...r,
    siteId: r.siteId || siteId,
    name: (raw.referentialName as string | undefined) ?? r.name ?? "",
    isArchived,
    archivedAt: isArchived ? (r.updatedAt ?? null) : (r.archivedAt ?? null),
    measuresCount: r.measuresCount ?? (raw.measureCount as number | undefined),
    existingMeasuresCount:
      r.existingMeasuresCount ??
      (raw.existingMeasureCount as number | undefined) ??
      0,
    toBeImplementedMeasuresCount:
      r.toBeImplementedMeasuresCount ??
      (raw.toBeImplementedMeasureCount as number | undefined) ??
      0,
  };
}

function buildRiskQuery(params: RiskListParams = {}) {
  return {
    Page: params.page,
    PageSize: params.pageSize,
    WorkUnitId: params.workUnitId,
    WorkPhaseId: params.workPhaseId,
    familyId: params.familyId,
    MinScore: params.minScore,
    MaxScore: params.maxScore,
    year: params.year,
    Year: params.year,
    IncludeArchived: params.includeArchived,
  };
}

export async function getRisks(
  siteId: string,
  params?: RiskListParams,
): Promise<Risk[]> {
  // Keep query parameter names aligned with the API contract.
  const baseQuery = buildRiskQuery(params);

  // Expanded work-unit panels use one server-side all-results request.
  if (params?.fetchAll && params.workUnitId) {
    const { data } = await apiClient.get<Risk[] | ApiPaginatedRisks>(
      `/api/sites/${siteId}/risks`,
      {
        params: {
          ...baseQuery,
          Page: 1,
          PageSize: API_LIST_PAGE_SIZE,
          all: true,
        },
      },
    );
    const raw = Array.isArray(data) ? data : (data.items ?? []);
    return raw.map((risk) => mapApiRisk(risk, siteId));
  }

  // fetchAll : récupère toutes les pages séquentiellement
  if (params?.fetchAll) {
    const all = await fetchAllPagesFrom<Risk>(async (page, pageSize) => {
      const { data } = await apiClient.get<Risk[] | ApiPaginatedRisks>(
        `/api/sites/${siteId}/risks`,
        {
          params: {
            ...(baseQuery ?? {}),
            Page: page,
            PageSize: pageSize,
          },
        },
      );

      return Array.isArray(data)
        ? { items: data }
        : {
            items: data.items ?? [],
            totalPages: data.pagination?.totalPages,
            pageSize: data.pagination?.pageSize,
          };
    }, API_LIST_PAGE_SIZE);

    return all.map((r) => mapApiRisk(r, siteId));
  }

  const { data } = await apiClient.get<Risk[] | ApiPaginatedRisks>(
    `/api/sites/${siteId}/risks`,
    { params: baseQuery },
  );
  const raw = Array.isArray(data) ? data : (data.items ?? []);
  return raw.map((risk) => mapApiRisk(risk, siteId));
}

export async function getRisk(siteId: string, riskId: string): Promise<Risk> {
  const { data } = await apiClient.get<Risk>(
    `/api/sites/${siteId}/risks/${riskId}`,
  );
  return data;
}

interface RawRiskDetailMeasure {
  id: string;
  category: string;
  type: string | null;
  description: string;
  status: string;
  deadline: string | null;
  isOverdue?: boolean | null;
  assigneeName: string | null;
  ressourcePlanningType: number | null;
  budgetType: number | null;
  budgetAmount: number | null;
}

interface RawRiskDetailsResponse {
  measures: RawRiskDetailMeasure[];
}

const RISK_DETAIL_STATUS_MAP: Record<string, PreventionPlanMeasureSummary["status"]> = {
  ToBePlanned: "ToBePlanned",
  Planned: "ToBePlanned",
  Todo: "ToBePlanned",
  InProgress: "InProgress",
  Done: "Done",
  Overdue: "ToBePlanned",
};

function normalizeRiskDetailMeasure(
  measure: RawRiskDetailMeasure,
): PreventionPlanMeasureSummary {
  return {
    id: measure.id,
    category: measure.category as PreventionPlanMeasureSummary["category"],
    type: measure.type as PreventionPlanMeasureSummary["type"],
    description: measure.description,
    status: RISK_DETAIL_STATUS_MAP[measure.status] ?? "ToBePlanned",
    deadline: measure.deadline,
    isOverdue: measure.isOverdue ?? measure.status === "Overdue",
    assigneeName: measure.assigneeName,
    ressourcePlanningType:
      measure.ressourcePlanningType != null
        ? (INT_TO_RESSOURCE_TYPE[measure.ressourcePlanningType] ?? null)
        : null,
    budgetType:
      measure.budgetType != null
        ? (INT_TO_BUDGET_TYPE[measure.budgetType] ?? null)
        : null,
    budgetAmount: measure.budgetAmount,
  };
}

export async function getRiskDetails(
  siteId: string,
  riskId: string,
): Promise<PreventionPlanRiskDetails> {
  const { data } = await apiClient.get<RawRiskDetailsResponse>(
    `/api/sites/${siteId}/risks/${riskId}`,
  );

  return {
    measures: (data.measures ?? []).map(normalizeRiskDetailMeasure),
  };
}

export async function createRisk(
  siteId: string,
  body: CreateRiskRequest,
): Promise<Risk> {
  const { data } = await apiClient.post<Risk>(
    `/api/sites/${siteId}/risks`,
    toApiBody(body),
  );
  return data;
}

export async function updateRisk(
  siteId: string,
  riskId: string,
  body: UpdateRiskRequest,
): Promise<Risk> {
  const { data } = await apiClient.patch<Risk>(
    `/api/sites/${siteId}/risks/${riskId}`,
    toApiBody(body),
    { headers: { "Content-Type": "application/merge-patch+json" } },
  );
  return data;
}

export async function patchRiskWorkPhase(
  siteId: string,
  riskId: string,
  workPhaseId: string | null,
): Promise<Risk> {
  const { data } = await apiClient.patch<Risk>(
    `/api/sites/${siteId}/risks/${riskId}`,
    { workPhaseId },
    { headers: { "Content-Type": "application/merge-patch+json" } },
  );
  return data;
}

export async function archiveRisk(
  siteId: string,
  riskId: string,
): Promise<void> {
  await apiClient.delete(`/api/sites/${siteId}/risks/${riskId}`);
}

export async function getRiskHistory(
  _siteId: string,
  riskId: string,
): Promise<RiskHistoryEntry[]> {
  const { data } = await apiClient.get<
    RiskHistoryEntry[] | { items: RiskHistoryEntry[] }
  >(`/api/risks/${riskId}/history`, {
    params: { page: 1, pageSize: 200 },
  });
  return Array.isArray(data) ? data : (data.items ?? []);
}
